import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Component, ReactNode } from 'react'

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null }
  static getDerivedStateFromError(error: Error) { return { error } }
  render() {
    if (this.state.error) {
      const err = this.state.error as Error
      return (
        <div style={{ padding: 32, fontFamily: 'monospace' }}>
          <h2 style={{ color: 'red' }}>Erreur de rendu</h2>
          <pre style={{ background: '#fee', padding: 16, borderRadius: 8, whiteSpace: 'pre-wrap' }}>
            {err.message}{'\n\n'}{err.stack}
          </pre>
          <button onClick={() => window.location.reload()} style={{ marginTop: 16, padding: '8px 16px' }}>
            Recharger
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import Planning from './pages/Planning'
import Employees from './pages/Employees'
import Absences from './pages/Absences'
import Timesheets from './pages/Timesheets'
import Positions from './pages/Positions'
import Payroll from './pages/Payroll'
import Documents from './pages/Documents'
import PaySlips from './pages/PaySlips'
import Export from './pages/Export'
import Assistant from './pages/Assistant'
import Establishments from './pages/Establishments'
import Badge from './pages/Badge'
import Layout from './components/Layout'
import { EstablishmentProvider } from './contexts/EstablishmentContext'
import MobileLogin from './pages/mobile/MobileLogin'
import MobileHome from './pages/mobile/MobileHome'
import MobilePlanning from './pages/mobile/MobilePlanning'
import MobileClock from './pages/mobile/MobileClock'
import MobileAbsences from './pages/mobile/MobileAbsences'
import MobileDocuments from './pages/mobile/MobileDocuments'
import MobilePaySlips from './pages/mobile/MobilePaySlips'
import MobileTimesheets from './pages/mobile/MobileTimesheets'
import MobileNotifications from './pages/mobile/MobileNotifications'
import Users from './pages/Users'
import EmployeeProfile from './pages/EmployeeProfile'
import Settings from './pages/Settings'
import Reporting from './pages/Reporting'
import ManagerLogin from './pages/mobile/ManagerLogin'
import ManagerHome from './pages/mobile/ManagerHome'
import ManagerPlanning from './pages/mobile/ManagerPlanning'
import ManagerAbsences from './pages/mobile/ManagerAbsences'
import ManagerTimesheets from './pages/mobile/ManagerTimesheets'
import ManagerTeam from './pages/mobile/ManagerTeam'
import ManagerBadges from './pages/mobile/ManagerBadges'
import ForgotPassword from './pages/ForgotPassword'
import IntegrationLogs from './pages/IntegrationLogs'
import ClockLive from './pages/ClockLive'
import EmployeeOnboarding from './pages/EmployeeOnboarding'
import Contracts from './pages/Contracts'
import TimesheetSign from './pages/TimesheetSign'
import Tasks from './pages/Tasks'
import Missions from './pages/Missions'
import MobileTasks from './pages/mobile/MobileTasks'
import MobileMissions from './pages/mobile/MobileMissions'
import MobileBvp from './pages/mobile/MobileBvp'
import MobileBvpControl from './pages/mobile/MobileBvpControl'
import MobileBvpPhotos from './pages/mobile/MobileBvpPhotos'
import AppTokenLogin from './pages/mobile/AppTokenLogin'
import Bvp from './pages/Bvp'
import MobileMessages from './pages/mobile/MobileMessages'
import MobileMessageThread from './pages/mobile/MobileMessageThread'
import MobileJobSheets from './pages/mobile/MobileJobSheets'
import MobileTeamPlanning from './pages/mobile/MobileTeamPlanning'
import KioskSetup from './pages/mobile/KioskSetup'
import KioskWatcher from './components/KioskWatcher'
import JobSheets from './pages/JobSheets'
import SheetsSync from './pages/SheetsSync'
import MobileTemperatures from './pages/mobile/MobileTemperatures'
import MobileReception from './pages/mobile/MobileReception'
import Conformite from './pages/Conformite'
import { ModuleRoute, EmployeeModuleRoute, ModuleKey } from './lib/modules'

// Raccourcis de garde par module (pages conservées, accès coupé si module éteint)
const M = (module: ModuleKey, el: JSX.Element) => <ModuleRoute module={module}>{el}</ModuleRoute>
const EM = (module: ModuleKey, el: JSX.Element) => <EmployeeModuleRoute module={module}>{el}</EmployeeModuleRoute>
const MM = (module: ModuleKey, el: JSX.Element) => <ModuleRoute module={module} redirect="/manager/home">{el}</ModuleRoute>

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const token = localStorage.getItem('token')
  return token ? <>{children}</> : <Navigate to="/login" replace />
}

function EmployeeRoute({ children }: { children: React.ReactNode }) {
  const token = localStorage.getItem('employeeToken')
  return token ? <>{children}</> : <Navigate to="/app/login" replace />
}

export default function App() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      {/* Tablette du magasin : déconnexion automatique après inactivité */}
      <KioskWatcher />
      <Routes>
        {/* Manager app */}
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/timesheets/sign/:token" element={<TimesheetSign />} />
        <Route path="/badge" element={<Badge />} />
        <Route path="/" element={<PrivateRoute><EstablishmentProvider><Layout /></EstablishmentProvider></PrivateRoute>}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="planning" element={<Planning />} />
          <Route path="employees" element={<Employees />} />
          <Route path="absences" element={M('conges', <Absences />)} />
          <Route path="timesheets" element={M('rh', <Timesheets />)} />
          <Route path="positions" element={<Positions />} />
          <Route path="payroll" element={M('rh', <Payroll />)} />
          <Route path="documents" element={M('rh', <Documents />)} />
          <Route path="payslips" element={M('rh', <PaySlips />)} />
          <Route path="export" element={M('rh', <Export />)} />
          <Route path="assistant" element={M('assistant', <Assistant />)} />
          <Route path="establishments" element={<Establishments />} />
          <Route path="employees/:id"  element={<EmployeeProfile />} />
          <Route path="users"          element={<Users />} />
          <Route path="settings"          element={<Settings />} />
          <Route path="reporting"         element={<Reporting />} />
          <Route path="contracts"         element={M('rh', <Contracts />)} />
          <Route path="integration-logs"  element={M('rh', <IntegrationLogs />)} />
          <Route path="clock-live"        element={M('badgeuse', <ClockLive />)} />
          <Route path="onboarding"        element={M('rh', <EmployeeOnboarding />)} />
          <Route path="onboarding/:id"    element={M('rh', <EmployeeOnboarding />)} />
          <Route path="tasks"             element={<Tasks />} />
          <Route path="missions"          element={<Missions />} />
          <Route path="bvp"               element={<Bvp />} />
          <Route path="qse"               element={<Conformite />} />
          <Route path="job-sheets"        element={<JobSheets />} />
          <Route path="sheets-sync"       element={M('planningAuto', <SheetsSync />)} />
        </Route>

        {/* Employee mobile app */}
        <Route path="/app/login" element={<MobileLogin />} />
        <Route path="/app/tablette" element={<KioskSetup />} />
        <Route path="/app/home"          element={<EmployeeRoute><MobileHome /></EmployeeRoute>} />
        <Route path="/app/planning"      element={<EmployeeRoute><MobilePlanning /></EmployeeRoute>} />
        <Route path="/app/team"          element={<EmployeeRoute><MobileTeamPlanning /></EmployeeRoute>} />
        <Route path="/app/clock"         element={<EmployeeRoute>{EM('badgeuse', <MobileClock />)}</EmployeeRoute>} />
        <Route path="/app/absences"      element={<EmployeeRoute>{EM('conges', <MobileAbsences />)}</EmployeeRoute>} />
        <Route path="/app/documents"     element={<EmployeeRoute>{EM('rh', <MobileDocuments />)}</EmployeeRoute>} />
        <Route path="/app/payslips"      element={<EmployeeRoute>{EM('rh', <MobilePaySlips />)}</EmployeeRoute>} />
        <Route path="/app/timesheets"    element={<EmployeeRoute>{EM('rh', <MobileTimesheets />)}</EmployeeRoute>} />
        <Route path="/app/notifications" element={<EmployeeRoute><MobileNotifications /></EmployeeRoute>} />
        <Route path="/app/tasks"         element={<EmployeeRoute><MobileTasks /></EmployeeRoute>} />
        <Route path="/app/temperatures"  element={<EmployeeRoute><MobileTemperatures /></EmployeeRoute>} />
        <Route path="/app/livraison"     element={<EmployeeRoute><MobileReception /></EmployeeRoute>} />
        <Route path="/app/missions"      element={<EmployeeRoute><MobileMissions /></EmployeeRoute>} />
        <Route path="/app/bvp"           element={<EmployeeRoute><MobileBvp /></EmployeeRoute>} />
        <Route path="/app/bvp/control"   element={<EmployeeRoute><MobileBvpControl /></EmployeeRoute>} />
        <Route path="/app/bvp/photos"    element={<EmployeeRoute><MobileBvpPhotos /></EmployeeRoute>} />
        <Route path="/app/job-sheets"      element={<EmployeeRoute><MobileJobSheets /></EmployeeRoute>} />
        <Route path="/app/messages"       element={<EmployeeRoute><MobileMessages /></EmployeeRoute>} />
        <Route path="/app/messages/:partnerId" element={<EmployeeRoute><MobileMessageThread /></EmployeeRoute>} />
        <Route path="/app/e/:token" element={<AppTokenLogin />} />
        <Route path="/app" element={<Navigate to="/app/login" replace />} />

        {/* Manager mobile app */}
        <Route path="/manager/login"      element={<ManagerLogin />} />
        <Route path="/manager/home"       element={<PrivateRoute><ManagerHome /></PrivateRoute>} />
        <Route path="/manager/planning"   element={<PrivateRoute><ManagerPlanning /></PrivateRoute>} />
        <Route path="/manager/absences"   element={<PrivateRoute>{MM('conges', <ManagerAbsences />)}</PrivateRoute>} />
        <Route path="/manager/timesheets" element={<PrivateRoute>{MM('rh', <ManagerTimesheets />)}</PrivateRoute>} />
        <Route path="/manager/team"       element={<PrivateRoute><ManagerTeam /></PrivateRoute>} />
        <Route path="/manager/badges"     element={<PrivateRoute>{MM('badgeuse', <ManagerBadges />)}</PrivateRoute>} />
        <Route path="/manager"            element={<Navigate to="/manager/login" replace />} />
      </Routes>
    </BrowserRouter>
    </ErrorBoundary>
  )
}
